import React, { useState } from 'react'
import { EmcNetworkScanButton } from '../../../components/Dialogs'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Emc, HeatpumpIo } from 'src/Libs/flow/installation/forms'
import { formatHeatpumpPairingId, isValidHeatpumpPairingId } from '../../utils/pairing'
import { HeatpumpPairingScanner } from './HeatpumpPairingScanner'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { TextField, Box } from '@mui/material'
import { useDialogActions } from '../../../components/Dialogs/hooks'
import { QrPairingEditor, QrScanButton } from 'src/Libs/installations'

export interface HeatpumpPairingDialogProps {
  installationId: number
  emc: Emc
  heatpump: HeatpumpIo
  submitPairingId: (id: string) => void
  onClose?: () => void
}

/**
 * Dialog to pair a heatpump by entering its pairing ID, scanning a QR code, or using network scanning.
 */
export const HeatpumpPairingDialog: React.FC<HeatpumpPairingDialogProps> = ({ installationId, emc, heatpump, submitPairingId, onClose }) => {
  const { t } = useCustomTranslation()

  const [pairingId, setPairingId] = useState(heatpump.pairingId)

  const isInvalidHeatpumpPairingId = pairingId && !isValidHeatpumpPairingId(pairingId)

  const error = isInvalidHeatpumpPairingId ? t('installation.productEditor.pairingEditor.invalid-heatpump-pairing-id') : undefined

  const isSame = heatpump.pairingId === pairingId

  const handleSubmit = (): void => {
    /** Dashes and semicolons are allowed during typing, but must be removed on submission. */
    const validPairingId = formatHeatpumpPairingId(pairingId)
    if (!validPairingId) return

    submitPairingId(validPairingId)

    onClose?.()
  }

  const handleOnClose = (): void => {
    onClose?.()
  }

  const [startQrScanner, setStartQrScanner] = useState(false)
  const [startNetworkScanner, setStartNetworkScanner] = useState(false)

  const primaryName = heatpump.isNew ? t('installation.productEditor.pairingEditor.save') : t('installation.productEditor.pairingEditor.pairAndSave')
  const dialogActions = useDialogActions({
    primary: {
      name: primaryName,
      onClick: handleSubmit,
      disabled: !!error || isSame,
    },
    secondary: { onClick: handleOnClose },
  })

  return (
    <Dialog maxWidth="md" onClose={handleOnClose} title={t('installation.productEditor.pairingEditor.title.heatpump')} actions={dialogActions}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, my: 2 }}>
        <TextField
          value={pairingId}
          name="heatpump pairing id"
          label={t('productEditor.pairingEditor.pairingID.heatpump')}
          fullWidth
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPairingId(e.target.value)}
          inputProps={{ maxLength: 50 }}
          error={!!error}
          helperText={error}
        />
        <EmcNetworkScanButton emc={emc} installationId={installationId} onClick={() => setStartNetworkScanner(!startNetworkScanner)} />
        <QrScanButton onClick={() => setStartQrScanner(!startQrScanner)} />
      </Box>

      <QrPairingEditor startQrScanner={startQrScanner} setStartQrScanner={setStartQrScanner} setPairingId={setPairingId} />

      {startNetworkScanner && (
        <Box>
          <HeatpumpPairingScanner
            installationId={installationId}
            emcId={emc.deviceId.toString()}
            currentPairingId={pairingId}
            setPairingId={setPairingId}
          />
        </Box>
      )}
    </Dialog>
  )
}
